import { createFileRoute } from "@tanstack/react-router";
import { localProductionSection } from "@/lib/seo/local-production";
import { LandingPage, type LandingFAQ } from "@/components/site/landing/LandingPage";
import { breadcrumbs, faqPage, serviceSchema, speakable, aeoGeo } from "@/lib/seo/schema";

const FAQ: LandingFAQ[] = [
  {
    q: "Vad kostar eventfilm i Malmö?",
    a: "En dag med en fotograf och klipp ligger på 30 000–60 000 kr. Konferens med multikamera, scenljud och intervjuer: 80 000–250 000 kr. Alla priser exklusive moms.",
  },
  {
    q: "Har ni team i Malmö och Skåne?",
    a: "Ja, vi producerar löpande i Malmö, Lund och övriga Skåne. Det gör att restid och riggtid hålls nere och att kort varsel ofta går att lösa.",
  },
  {
    q: "Kan vi få material samma dag?",
    a: "Ja. Vi klipper på plats och levererar social-cut under eventdagen. Huvudfilm och intervjuklipp kommer inom 1–2 veckor.",
  },
  {
    q: "Filmar ni på Malmömässan och andra lokaler?",
    a: "Vi arbetar i Malmös mäss- och konferensmiljöer. Vi gör alltid en teknisk recce av ljus, ljud och kameraplaceringar innan eventdagen.",
  },
  {
    q: "Täcker ni event i Köpenhamnsregionen?",
    a: "Ja, vi filmar även på danska sidan av Öresund när eventet ligger där. Rese- och eventuella tullkostnader specificeras i offerten.",
  },
  {
    q: "Hur många kameror behövs?",
    a: "Mindre kundträff klarar sig med en kamera. Har eventet scen med talare rekommenderar vi två till tre kameror så att klippningen blir användbar och ljudet konsekvent.",
  },
];

export const Route = createFileRoute("/eventfilm_/malmo")({
  head: () => ({
    meta: [
      { title: "Eventfilm Malmö — same-day leverans och pris | Fenny" },
      {
        name: "description",
        content:
          "Eventfilm i Malmö och Skåne för konferenser, mässor och lanseringar. Same-day social-cut, huvudfilm inom 1–2 veckor. Pris 30 000–250 000 kr.",
      },
      { property: "og:title", content: "Eventfilm Malmö — Fenny" },
      {
        property: "og:description",
        content:
          "Eventproduktion i Malmö med same-day leverans, multikamera och talarintervjuer.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://fenny.se/eventfilm/malmo" },
    ],
    links: [{ rel: "canonical", href: "https://fenny.se/eventfilm/malmo" }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(aeoGeo({path: "/eventfilm/malmo", name: "Eventfilm Malmö — same-day leverans och pris | Fenny", description: "Eventfilm i Malmö och Skåne för konferenser, mässor och lanseringar. Same-day social-cut, huvudfilm inom 1–2 veckor. Pris 30 000–250 000 kr."})) },
      { type: "application/ld+json", children: JSON.stringify(speakable([".lede"])) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          serviceSchema({
            name: "Eventfilm Malmö",
            path: "/eventfilm/malmo",
            serviceType: "Eventfilm-produktion",
            description:
              "Eventfilm i Malmö och Skåne för konferenser, mässor, lanseringar och kundträffar — same-day social-cut och huvudfilm.",
            priceRange: "30 000–250 000 kr",
            areaServed: ["Malmö"],
          }),
        ),
      },
      { type: "application/ld+json", children: JSON.stringify(faqPage(FAQ)) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          breadcrumbs([
            { name: "Hem", path: "/" },
            { name: "Eventfilm", path: "/eventfilm" },
            { name: "Malmö", path: "/eventfilm/malmo" },
          ]),
        ),
      },
    ],
  }),
  component: EventfilmMalmoPage,
});

function EventfilmMalmoPage() {
  return (
    <LandingPage
      eyebrow="Eventfilm Malmö"
      title={<>Eventfilm i Malmö — en dag som ger innehåll hela kvartalet.</>}
      lede="Fenny filmar konferenser, mässor, lanseringar och kundträffar i Malmö, Lund och övriga Skåne. Social-cut levereras samma dag, huvudfilm och talarintervjuer inom 1–2 veckor."
      tldr={
        <p>
          Eventfilm i Malmö kostar <strong>30 000–60 000 kr</strong> för en dag
          med en fotograf och klipp, och <strong>80 000–250 000 kr</strong> för
          multikamera med scenljud, intervjuer och versionering. Same-day
          social-cut ingår.
        </p>
      }
      ctaPrimary={{ label: "Boka för ert event", to: "/kontakt" }}
      ctaSecondary={{ label: "Se case", to: "/case" }}
      trackingId="eventfilm-malmo"
      bullets={[
        { title: "Same-day social", body: "Färdiga reels medan eventet pågår." },
        { title: "Skåne och Öresund", body: "Vi producerar löpande i Malmö och Lund — även danska sidan." },
        { title: "Scenljud direkt", body: "Vi kopplar mot husets ljudbord för användbart talarljud." },
      ]}
      pricing={{
        heading: "Vad kostar eventfilm i Malmö?",
        intro: "Riktpriser exklusive moms. Exakt pris sätts efter agenda och lokal.",
        rows: [
          { format: "Kundträff / mindre event", scope: "1 fotograf, 1 dag, social-cut + highlight", price: "30 000–60 000 kr", lead: "same-day + 1 vecka" },
          { format: "Konferens med scen", scope: "2 kameror, scenljud, talarintervjuer", price: "80 000–150 000 kr", lead: "same-day + 2 veckor" },
          { format: "Mässa / lansering", scope: "3 kameror, producent, B-roll-bibliotek, flera versioner", price: "150 000–250 000 kr", lead: "same-day + 2 veckor" },
        ],
        footnote: "Alla priser exklusive moms. Återkommande event samlas med fördel i ett abonnemang.",
      }}
      sections={[
        localProductionSection("malmo", "Malmö"),
        {
          id: "upplagg",
          heading: "Så lägger vi upp en eventdag i Malmö",
          body: (
            <p>
              Recce av lokalen innan eventet: ljus, ljud, kameraplaceringar och
              intervjuplats. Under dagen filmar vi scen, mingel och detaljer
              medan en redigerare klipper social-materialet på plats. Pauserna
              används till talar- och kundintervjuer som blir egna{" "}
              <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/kundcase-film">
                kundcase
              </a>
              .
            </p>
          ),
        },
        {
          id: "efterat",
          heading: "Vad materialet används till efteråt",
          body: (
            <p>
              Highlight-filmen säljer in nästa edition. Talarklippen blir
              innehåll i 4–8 veckor. B-roll används i kampanjer, rekrytering och
              på sajten. Se även{" "}
              <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/filmproduktion/malmo">
                filmproduktion i Malmö
              </a>{" "}
              för övriga format.
            </p>
          ),
        },
        {
          id: "ovriga-orter",
          heading: "Andra orter",
          body: (
            <p>
              Vi filmar event i hela Sverige — se{" "}
              <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/eventfilm/stockholm">
                eventfilm i Stockholm
              </a>
              ,{" "}
              <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/eventfilm/goteborg">
                eventfilm i Göteborg
              </a>{" "}
              eller{" "}
              <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/eventfilm">
                eventfilm generellt
              </a>
              .
            </p>
          ),
        },
      ]}
      faq={FAQ}
      caseLimit={3}
      riskReducers
      closing={{
        title: "Event i Malmö på gång?",
        body: "Boka 30 minuter så planerar vi täckning, leverans och pris.",
        primary: { label: "Boka möte", to: "/kontakt" },
        secondary: { label: "Se priser", to: "/priser" },
      }}
    />
  );
}
